import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Img } from 'ui-kit';
import { CT_SYMBOL, NOTIFICATION_BELL_ICON } from 'assets/icons';
import { AuthEntity, CollectEntity, NotificationEntity } from '_entities';
import { NotificationModal, UserInformationModal } from 'modules/Modals';
import LogOutPopup from 'modules/Navigation/MobileChrome/components/LogOutPopup';
import { IsAzAccount } from 'modules';
import { usePopover, useRefreshNotificationsOnMediaExport } from 'hooks';
import { idbService, notification } from 'utils/services';
import { removeToken } from 'utils/services/token';
import { signOut } from '_entities/Auth/Auth.actions';
import { baseUrl } from 'lib/baseUrl';
import { PERMISSIONS, URLS, USER_ROLE_TYPES } from '_constants';

import { getMenuRoutes, filterRoutesByPermissions } from '../_utils';
import SubPagesPopup from '../SubPagesPopup';
import * as Styled from './styles';

const {
  getAuthenticatedUser,
  getUserEmail,
  getUserFirstName,
  getUserLastName,
  getAccountProductInfo,
  getProfilePermissions,
  getModularityPermissions,
  getUserRoleType,
} = AuthEntity.selectors;
const { setCollectCount } = CollectEntity.actions;
const { getNotificationList } = NotificationEntity.actions;
const { getNotificationMessages, getUnreadMessages } = NotificationEntity.selectors;

const REPORT_POLL_TTL_MS = 10 * 60 * 1000;
const REPORT_POLL_INTERVAL_MS = 20_000;

const checkPollingTimeout = () => {
  const lastReportTime = localStorage.getItem('lastReportTime');
  if (!lastReportTime) return false;
  const last = Number(lastReportTime);
  if (!Number.isFinite(last)) return false;
  return Date.now() < last + REPORT_POLL_TTL_MS;
};

const hasPendedReports = () => {
  try {
    const generatedReports = JSON.parse(localStorage.getItem('generatedReports') || '[]');
    return generatedReports.length > 0;
  } catch {
    return false;
  }
};

const fetchTaskStatus = (taskId) => {
  const token = localStorage.getItem('Authorization');
  return axios.get(`${baseUrl}tasks/${taskId}/status/`, {
    headers: {
      Authorization: `Token ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/vnd.api+json',
    },
  });
};

const dropTaskIdFromStorage = (taskId) => {
  const generatedReports = JSON.parse(localStorage.getItem('generatedReports') || '[]');
  const remaining = generatedReports.filter((id) => id !== taskId);
  if (remaining.length === 0) {
    localStorage.removeItem('generatedReports');
    localStorage.removeItem('lastReportTime');
  } else {
    localStorage.setItem('generatedReports', JSON.stringify(remaining));
  }
};

// One-shot auto-focus: TopBar remounts on every page navigation (PageContainer is per-page),
// so we focus the trigger only the first time it mounts in this browser session.
let hasAutoFocusedTrigger = false;

const initialsFromName = (firstName, lastName, email) => {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  if (f || l) return `${f[0] || ''}${l[0] || ''}`.toUpperCase() || '??';
  if (!email) return '??';
  const local = email.split('@')[0] || '';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
};

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.25" />
    <circle cx="8" cy="5" r="0.85" fill="currentColor" />
    <path d="M8 7.5v4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
  </svg>
);

const UserAddIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="6.5" cy="5.25" r="2.5" stroke="currentColor" strokeWidth="1.25" />
    <path
      d="M2 13c0-2.2 2-3.75 4.5-3.75S11 10.8 11 13"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
    />
    <path d="M12.5 8.25v3M14 9.75h-3" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M9.5 3.5V2.75A1.25 1.25 0 0 0 8.25 1.5h-4A1.25 1.25 0 0 0 3 2.75v10.5a1.25 1.25 0 0 0 1.25 1.25h4a1.25 1.25 0 0 0 1.25-1.25V12.5"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
    />
    <path
      d="M6.5 8h7.5M11.5 5.5 14 8l-2.5 2.5"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="m2 3.75 3 3 3-3" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TopBar = ({ onOpenPalette }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const isAuthenticated = useSelector(getAuthenticatedUser);
  const userEmail = useSelector(getUserEmail);
  const firstName = useSelector(getUserFirstName);
  const lastName = useSelector(getUserLastName);
  const accountProduct = useSelector(getAccountProductInfo);
  const userPermissions = useSelector(getProfilePermissions);
  const modularityPermissions = useSelector(getModularityPermissions);
  const userRoleType = useSelector(getUserRoleType);
  const notifications = useSelector(getNotificationMessages);
  const unreadMessages = useSelector(getUnreadMessages);
  const isAzAccount = IsAzAccount();

  const [isNotificationsOpen, setNotificationsOpen] = useState(false);
  const [isUserInfoOpen, setUserInfoOpen] = useState(false);
  const [isLogOutOpen, setLogOutOpen] = useState(false);
  const pollIntervalRef = useRef(null);
  const triggerRef = useRef(null);
  const accountWrapperRef = useRef(null);
  const accountPanelRef = useRef(null);
  const publicWrapperRef = useRef(null);
  const publicPanelRef = useRef(null);
  const {
    open: isAccountOpen,
    setOpen: setAccountOpen,
    close: closeAccount,
  } = usePopover({
    wrapperRef: accountWrapperRef,
    panelRef: accountPanelRef,
  });
  const {
    open: isPublicOpen,
    setOpen: setPublicOpen,
    close: closePublic,
  } = usePopover({
    wrapperRef: publicWrapperRef,
    panelRef: publicPanelRef,
  });

  const publicSection = useMemo(() => {
    if (isAuthenticated) return null;
    const routes = getMenuRoutes(false, '', false, [], isAzAccount);
    const filtered = filterRoutesByPermissions(routes, userPermissions, modularityPermissions);
    const section = filtered.find((r) => r.id === 9);
    if (!section?.subRoutes?.length) return null;
    return { title: 'Public Access', subRoutes: section.subRoutes };
  }, [isAuthenticated, isAzAccount, userPermissions, modularityPermissions]);

  const goToLogin = useCallback(() => history.push(URLS.login), [history]);

  useEffect(() => {
    if (hasAutoFocusedTrigger || !isAuthenticated) return;
    triggerRef.current?.focus();
    hasAutoFocusedTrigger = true;
  }, [isAuthenticated]);

  const loadNotifications = useCallback(async () => {
    try {
      await dispatch(getNotificationList());
    } catch {
      // Non-fatal; bell badge stays at its last value.
    }
  }, [dispatch]);

  const handleBellClick = useCallback(() => setNotificationsOpen((prev) => !prev), []);
  const handleAvatarClick = useCallback(() => setAccountOpen((prev) => !prev), [setAccountOpen]);
  const closeNotifications = useCallback(() => setNotificationsOpen(false), []);
  const closeUserInfo = useCallback(() => setUserInfoOpen(false), []);

  const openUserDetails = useCallback(() => {
    closeAccount();
    setUserInfoOpen(true);
  }, [closeAccount]);

  const openLogOut = useCallback(() => {
    closeAccount();
    setLogOutOpen(true);
  }, [closeAccount]);

  const handleLoginAsUser = useCallback(async () => {
    closeAccount();
    try {
      await dispatch(signOut());
    } catch {
      // Non-fatal: token is still cleared and login flow continues in finally.
    } finally {
      removeToken();
      history.push(URLS.login, { as_user: true });
    }
  }, [closeAccount, dispatch, history]);

  const canLoginAsUser =
    userRoleType === USER_ROLE_TYPES.MULTI_ACCOUNT_MANAGER &&
    Array.isArray(userPermissions) &&
    userPermissions.includes(PERMISSIONS.LOGIN_AS_USER);

  useEffect(() => {
    const updateOfflineSurveyCount = async () => {
      const db = await idbService.initiateIDB();
      const count = await db.transaction('surveys').objectStore('surveys').count();
      await dispatch(setCollectCount(count));
    };
    updateOfflineSurveyCount();
    if (isAuthenticated) loadNotifications();
    // Mount-only effect; matches the pre-refactor behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps the bell current when a media export lands while the user stays on this page — the mount-only
  // fetch above is otherwise the last word until a reload or a route change.
  useRefreshNotificationsOnMediaExport(loadNotifications, isAuthenticated);

  useEffect(() => {
    if (unreadMessages && !isNotificationsOpen && isAuthenticated && pathname === URLS.dashboard) {
      const noun = unreadMessages > 1 ? 'messages' : 'message';
      notification.info({
        notification: `You have ${unreadMessages} unread ${noun}!`,
        onClick: handleBellClick,
      });
    }
  }, [unreadMessages, isNotificationsOpen, isAuthenticated, pathname, handleBellClick]);

  useEffect(() => {
    const pollOnce = async () => {
      if (!checkPollingTimeout()) {
        localStorage.removeItem('generatedReports');
        localStorage.removeItem('lastReportTime');
        return;
      }
      if (!hasPendedReports()) return;
      const generatedReports = JSON.parse(localStorage.getItem('generatedReports') || '[]');
      await Promise.all(
        generatedReports.map(async (taskId) => {
          try {
            const response = await fetchTaskStatus(taskId);
            const status = response?.data?.data?.status;
            if (status === 'SUCCESS' || status === 'ERROR') {
              dropTaskIdFromStorage(taskId);
              await loadNotifications();
              if (status === 'SUCCESS') {
                notification.info({
                  notification: 'Your report is successfully generated and ready to download!',
                  onClick: handleBellClick,
                  autoClose: { autoClose: false },
                });
              }
            }
          } catch {
            // Non-fatal; tick again on next interval.
          }
        }),
      );
      if (!hasPendedReports() && pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };

    if (isAuthenticated && checkPollingTimeout()) {
      pollOnce();
      pollIntervalRef.current = setInterval(pollOnce, REPORT_POLL_INTERVAL_MS);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, loadNotifications, handleBellClick]);

  const initials = useMemo(() => initialsFromName(firstName, lastName, userEmail), [firstName, lastName, userEmail]);
  const fullName = useMemo(() => {
    const joined = `${firstName || ''} ${lastName || ''}`.trim();
    return joined || userEmail || '';
  }, [firstName, lastName, userEmail]);
  const isMac = useMemo(() => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform), []);

  return (
    // data-app-header marks the fixed top chrome so useAnchoredPanel can hide portaled popovers that
    // would otherwise scroll over it (the panels outrank it on z-index by necessity).
    <Styled.TopBarContainer className="no-print" data-app-header>
      <Styled.LogoGroup>
        <Img alt="" src={CT_SYMBOL} size={[28, 28]} />
        <Styled.BrandName>Counter Tools</Styled.BrandName>
        {accountProduct && <Styled.PostWordmark>{accountProduct}</Styled.PostWordmark>}
      </Styled.LogoGroup>

      {!isAuthenticated && publicSection && (
        <Styled.AccountWrapper ref={publicWrapperRef}>
          <Styled.PublicTrigger
            type="button"
            onClick={() => setPublicOpen((prev) => !prev)}
            aria-haspopup="dialog"
            aria-expanded={isPublicOpen}
          >
            <span>Public Access</span>
            <Styled.AvatarChevron $open={isPublicOpen}>
              <ChevronIcon />
            </Styled.AvatarChevron>
          </Styled.PublicTrigger>

          {isPublicOpen && (
            <div ref={publicPanelRef}>
              <SubPagesPopup
                section={publicSection}
                currentPathname={pathname}
                onClose={closePublic}
                placement="below"
              />
            </div>
          )}
        </Styled.AccountWrapper>
      )}

      {isAuthenticated && (
        <Styled.SearchTrigger ref={triggerRef} type="button" onClick={onOpenPalette} aria-label="Open command palette">
          <Styled.SearchPlaceholder>Jump to anything · retailer, inspection, user…</Styled.SearchPlaceholder>
          <Styled.KbdChip>{isMac ? '⌘K' : 'Ctrl+K'}</Styled.KbdChip>
        </Styled.SearchTrigger>
      )}

      <Styled.RightCluster>
        {!isAuthenticated && (
          <Styled.LoginButton type="button" onClick={goToLogin}>
            Login
          </Styled.LoginButton>
        )}

        {isAuthenticated && (
          <Styled.IconButton type="button" onClick={handleBellClick} aria-label="Notifications">
            <Img size={[18, 18]} src={NOTIFICATION_BELL_ICON} alt="" />
            {Boolean(unreadMessages) && (
              <Styled.BellBadge>{unreadMessages <= 9 ? unreadMessages : '9+'}</Styled.BellBadge>
            )}
          </Styled.IconButton>
        )}

        {isAuthenticated && (
          <Styled.AccountWrapper ref={accountWrapperRef}>
            <Styled.AvatarTrigger
              type="button"
              onClick={handleAvatarClick}
              aria-haspopup="dialog"
              aria-expanded={isAccountOpen}
              aria-label="Account menu"
            >
              <Styled.AvatarCircle>{initials}</Styled.AvatarCircle>
              <Styled.AvatarChevron $open={isAccountOpen}>
                <ChevronIcon />
              </Styled.AvatarChevron>
            </Styled.AvatarTrigger>

            {isAccountOpen && (
              <Styled.AccountPanel ref={accountPanelRef} role="dialog" aria-label="Account">
                <Styled.AccountHeader>
                  <Styled.AccountHeaderAvatar>{initials}</Styled.AccountHeaderAvatar>
                  <Styled.AccountHeaderText>
                    {fullName && <Styled.AccountName>{fullName}</Styled.AccountName>}
                    {userEmail && <Styled.AccountEmail>{userEmail}</Styled.AccountEmail>}
                  </Styled.AccountHeaderText>
                </Styled.AccountHeader>

                <Styled.AccountDivider />

                <Styled.AccountItem type="button" onClick={openUserDetails}>
                  <Styled.AccountItemIcon>
                    <InfoIcon />
                  </Styled.AccountItemIcon>
                  <Styled.AccountItemLabel>User details</Styled.AccountItemLabel>
                </Styled.AccountItem>

                {canLoginAsUser && (
                  <Styled.AccountItem type="button" onClick={handleLoginAsUser}>
                    <Styled.AccountItemIcon>
                      <UserAddIcon />
                    </Styled.AccountItemIcon>
                    <Styled.AccountItemLabel>Login as user…</Styled.AccountItemLabel>
                  </Styled.AccountItem>
                )}

                <Styled.AccountDivider />

                <Styled.AccountItem type="button" onClick={openLogOut} $danger>
                  <Styled.AccountItemIcon>
                    <LogoutIcon />
                  </Styled.AccountItemIcon>
                  <Styled.AccountItemLabel>Log out</Styled.AccountItemLabel>
                </Styled.AccountItem>
              </Styled.AccountPanel>
            )}
          </Styled.AccountWrapper>
        )}
      </Styled.RightCluster>

      {isNotificationsOpen && (
        <NotificationModal
          onClose={closeNotifications}
          notificationList={notifications}
          loadNotifications={loadNotifications}
        />
      )}
      <UserInformationModal isOpen={isUserInfoOpen} onClose={closeUserInfo} />
      <LogOutPopup isOpenPopup={isLogOutOpen} setIsOpenPopup={setLogOutOpen} />
    </Styled.TopBarContainer>
  );
};

TopBar.propTypes = {
  onOpenPalette: PropTypes.func,
};

TopBar.defaultProps = {
  onOpenPalette: () => {},
};

export default TopBar;
